import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Hero = () => {
  // Fade-in animation variants for staggered load timing
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } 
    }
  };

  return (
    <section className="relative min-h-[85vh] flex flex-col items-center justify-center px-6 overflow-hidden">
      
      {/* 1. Ambient Background Layer (Top-tier SaaS Radial Glow) */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {/* Central Neon Electric Green Glow */}
        <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-electric-green/10 rounded-full blur-[120px]" />
        {/* Subtle contrasting blue depth flare */}
        <div className="absolute -top-[10%] left-[30%] w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[100px]" />
      </div>

      {/* Main Content Container */}
      <motion.div 
        initial="hidden"
        animate="visible"
        variants={{
          visible: { transition: { staggerChildren: 0.15 } }
        }}
        className="max-w-5xl mx-auto text-center relative z-10 flex flex-col items-center"
      >
        
        {/* 2. Premium Announcement Pill Badge */}
        <motion.div variants={fadeInUp} className="mb-8">
          <div className="inline-flex items-center gap-2.5 bg-white/5 backdrop-blur-md border border-white/10 px-4 py-1.5 rounded-full text-xs font-medium tracking-wide text-gray-300 hover:border-white/20 transition-all cursor-pointer group">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-electric-green opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-electric-green"></span>
            </span>
            <span>Next-Gen Cloud & Architecture</span>
            <ArrowRight className="w-3.5 h-3.5 text-gray-500 group-hover:text-electric-green group-hover:translate-x-0.5 transition-all" />
          </div>
        </motion.div>

        {/* 3. High-Contrast Cinematic Typography */}
        <motion.h1 
          variants={fadeInUp}
          className="text-5xl sm:text-6xl md:text-8xl font-black tracking-tighter text-white mb-8 leading-[0.95]"
        >
          Innovative IT solutions <br />
          <span className="bg-gradient-to-r from-white via-white to-electric-green bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(57,255,20,0.15)]">
            to propel your business
          </span>
        </motion.h1>
        
        {/* 4. Elegant, Minimalist Sub-headline */}
        <motion.p 
          variants={fadeInUp}
          className="text-base sm:text-lg md:text-xl text-gray-400 mb-12 max-w-2xl leading-relaxed font-normal tracking-wide"
        >
          Développement web, cybersécurité, maintenance informatique et support technique sur mesure pour transformer votre infrastructure.
        </motion.p>

        {/* 5. Dual Button Ecosystem (Action Framework) */}
        <motion.div 
          variants={fadeInUp}
          className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto"
        >
          {/* Primary Button: High Intensity Neon Action */}
          <Link 
            to="/contact" 
            className="w-full sm:w-auto px-8 py-4 bg-electric-green text-royal-navy font-bold text-base rounded-xl transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] shadow-[0_0_30px_rgba(57,255,20,0.25)] hover:shadow-[0_0_40px_rgba(57,255,20,0.4)] flex items-center justify-center gap-2"
          >
            Get a Quote
            <ArrowUpRight className="w-4 h-4 stroke-[2.5]" />
          </Link>

          {/* Secondary Button: Premium Glassmorphic Contrast */}
          <Link 
            to="/services" 
            className="w-full sm:w-auto px-8 py-4 bg-white/5 backdrop-blur-md border border-white/10 text-white font-semibold text-base rounded-xl transition-all duration-300 hover:bg-white/10 hover:border-white/20 active:scale-[0.98] flex items-center justify-center gap-2 group"
          >
            Explore Services
            <span className="text-gray-500 group-hover:text-white group-hover:translate-x-0.5 transition-all duration-300">→</span>
          </Link>
        </motion.div>

      </motion.div>
    </section>
  );
};

export default Hero;